import { MapPin, Phone, MessageCircle, Mail, Clock } from "lucide-react";
import { COMPANY } from "@/lib/company";

export default function VisitOffice() {
  const mapQuery = encodeURIComponent("Al Fattan Business Hub, Office Square, Dubai Marina, Dubai");
  const mapEmbedUrl = `https://www.google.com/maps?q=${mapQuery}&output=embed`;
  const mapDirectionsUrl = `https://www.google.com/maps/search/?api=1&query=${mapQuery}`;

  return (
    <section id="visit-office" data-testid="visit-office-section" className="py-20 lg:py-28 bg-[#F9F8F6]">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid lg:grid-cols-12 gap-8 lg:gap-12 items-stretch">
          <div className="lg:col-span-5 flex flex-col">
            <p className="text-[11px] uppercase tracking-[0.25em] text-[#C5A059] font-semibold mb-3">
              Visit Us
            </p>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-light tracking-tight text-neutral-900 mb-6 leading-[1.1]">
              Visit Our Dubai Marina Office
            </h2>
            <p className="text-base lg:text-lg text-neutral-600 leading-relaxed mb-8">
              Meet the {COMPANY.name} team in Dubai Marina to discuss your apartment, villa, penthouse, or commercial renovation project.
            </p>

            <div className="bg-white border border-neutral-200 p-6 lg:p-7 space-y-5">
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 bg-[#C5A059]/10 flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-4 h-4 text-[#C5A059]" />
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-[0.2em] text-neutral-400 mb-1">Office Address</p>
                  <p className="text-sm font-medium text-neutral-900 leading-relaxed" data-testid="office-address">
                    Al Fattan Business Hub, Office Square,<br />
                    Marsa Dubai Marina, Dubai, UAE
                  </p>
                </div>
              </div>

              <a href={`tel:${COMPANY.phoneIntl}`} data-testid="visit-office-phone" className="flex items-start gap-3 group">
                <div className="w-9 h-9 bg-[#C5A059]/10 flex items-center justify-center flex-shrink-0 group-hover:bg-[#C5A059] transition-colors">
                  <Phone className="w-4 h-4 text-[#C5A059] group-hover:text-white" />
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-[0.2em] text-neutral-400 mb-1">Phone</p>
                  <p className="text-sm font-medium text-neutral-900 group-hover:text-[#C5A059] transition-colors">{COMPANY.phone}</p>
                </div>
              </a>

              <a href={COMPANY.whatsappLink} target="_blank" rel="noopener noreferrer" data-testid="visit-office-whatsapp" className="flex items-start gap-3 group">
                <div className="w-9 h-9 bg-[#25D366]/10 flex items-center justify-center flex-shrink-0 group-hover:bg-[#25D366] transition-colors">
                  <MessageCircle className="w-4 h-4 text-[#25D366] group-hover:text-white" />
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-[0.2em] text-neutral-400 mb-1">WhatsApp</p>
                  <p className="text-sm font-medium text-neutral-900 group-hover:text-[#25D366] transition-colors">{COMPANY.whatsapp}</p>
                </div>
              </a>

              <a href={`mailto:${COMPANY.email}`} data-testid="visit-office-email" className="flex items-start gap-3 group">
                <div className="w-9 h-9 bg-[#C5A059]/10 flex items-center justify-center flex-shrink-0 group-hover:bg-[#C5A059] transition-colors">
                  <Mail className="w-4 h-4 text-[#C5A059] group-hover:text-white" />
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-[0.2em] text-neutral-400 mb-1">Email</p>
                  <p className="text-sm font-medium text-neutral-900 group-hover:text-[#C5A059] transition-colors">{COMPANY.email}</p>
                </div>
              </a>

              <div className="flex items-start gap-3 pt-2 border-t border-neutral-100">
                <div className="w-9 h-9 bg-neutral-100 flex items-center justify-center flex-shrink-0">
                  <Clock className="w-4 h-4 text-neutral-500" />
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-[0.2em] text-neutral-400 mb-1">Office Hours</p>
                  <p className="text-sm text-neutral-700">Sat – Thu · 9:00 AM – 7:00 PM</p>
                </div>
              </div>
            </div>

            <a href={mapDirectionsUrl} target="_blank" rel="noopener noreferrer" data-testid="visit-office-directions" className="inline-flex items-center justify-center gap-2 mt-5 bg-[#C5A059] hover:bg-[#A88746] text-white px-6 py-4 text-sm font-medium transition-colors">
              <MapPin className="w-4 h-4" /> Get Directions
            </a>
          </div>

          <div className="lg:col-span-7">
            <div className="relative h-[420px] lg:h-full min-h-[420px] bg-neutral-200 overflow-hidden border border-neutral-200">
              <iframe
                title="Fix Technical Renovation Company office – Dubai Marina"
                src={mapEmbedUrl}
                width="100%"
                height="100%"
                style={{ border: 0, minHeight: "420px" }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                data-testid="office-map-iframe"
              />
              <div className="pointer-events-none absolute top-4 left-4 bg-white px-4 py-3 shadow-lg max-w-[80%]">
                <p className="text-[10px] uppercase tracking-[0.2em] text-[#C5A059] mb-1 font-semibold">
                  Fix Technical Renovation Company
                </p>
                <p className="text-xs text-neutral-700 leading-relaxed">
                  Al Fattan Business Hub, Dubai Marina, UAE
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
